import { useCallback, useEffect, useState } from "react";
import { BellRing } from "lucide-react";

import { toast } from "@/shared/hooks/use-toast";
import { Skeleton } from "@/shared/ui/skeleton";
import { Switch } from "@/shared/ui/switch";

import { acqService, DAILY_CAP_DEFAULT, type AcqSubscription } from "../../api/acqService";
import { errorToast } from "@/shared/lib/apiErrors";

const PUSH_HOURS = [8, 9, 10, 11];

/**
 * 推送设置(契约 §12.3:`xb_acq_subscription` 只剩 push_enabled / push_hour / daily_cap)。
 *
 * ★ 这是**信号订阅页退役后剩下的那一块**:盯什么、去哪儿盯已经搬到每一种发现自己的卡上,
 *   这里只剩"跑出来的单子怎么送到你手上"。
 * ★★ 文案必须说出**没绑企微会怎样**:后端 `AcqPushJob` 没绑就跳过(契约 §12.9)。
 *   只写「早 9 点推给你」而不说这个前提,没绑的用户会等一个永远不来的消息,
 *   然后判定"这功能是坏的"。
 */
export function PushSettings() {
  const [sub, setSub] = useState<AcqSubscription | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    acqService.subscription().then(
      (s) => alive && setSub(s),
      () => alive && setSub(null)
    );
    return () => {
      alive = false;
    };
  }, []);

  const save = useCallback(
    async (patch: Partial<AcqSubscription>) => {
      const base: AcqSubscription = sub ?? {
        keywords: [],
        regions: [],
        industryCodes: [],
        sources: [],
        dailyCap: DAILY_CAP_DEFAULT,
        pushEnabled: false,
        pushHour: 9,
      };
      const next = { ...base, ...patch };
      setSub(next); // 乐观:开关是个手感动作,等一跳网络会让人以为没点上
      setSaving(true);
      try {
        setSub(await acqService.saveSubscription(next));
      } catch (e) {
        setSub(base); // 回滚到点之前,不留一个"看着开了其实没存上"的开关
        toast(errorToast(e, { fallbackTitle: "推送设置没保存上" }));
      } finally {
        setSaving(false);
      }
    },
    [sub]
  );

  if (sub === undefined) return <Skeleton className="h-24 w-full rounded-xl" />;

  const enabled = sub?.pushEnabled ?? false;
  const hour = sub?.pushHour ?? 9;

  return (
    <section className="rounded-xl border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <BellRing className="h-4 w-4 text-primary" />
            每天早上推给我
          </p>
          <p className="mt-1 max-w-xl text-2xs leading-relaxed text-muted-foreground">
            绑定企业微信应用后，早 {hour} 点把当天的单子推到手机；
            <span className="font-semibold text-foreground">
              没绑的时候单子只在「今日商机」这一页
            </span>
            ，不会丢，但也不会来找你。
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <select
            aria-label="推送时间"
            value={hour}
            disabled={!enabled || saving}
            onChange={(e) => void save({ pushHour: Number(e.target.value) })}
            className="h-9 rounded-md border bg-background px-2 text-sm disabled:opacity-50"
          >
            {PUSH_HOURS.map((h) => (
              <option key={h} value={h}>
                早 {h}:00
              </option>
            ))}
          </select>
          <Switch
            checked={enabled}
            disabled={saving}
            onCheckedChange={(pushEnabled) => void save({ pushEnabled })}
            aria-label="每天推送"
          />
        </div>
      </div>
    </section>
  );
}
